// ============================================
// i18n Configuration — 76 Languages
// Ported from: application/language/*/app_files/system_lang.php
// Usage: import { t } from '@school/shared/i18n';
//        t('student_admission') → "Student Admission" (or translated)
// ============================================
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Import all language JSON files
// These were auto-generated from the PHP `$lang['key'] = 'value'` files
import en from './locales/english.json';

// Language registry — maps language code to display name + JSON import
// We lazy-load non-English languages to keep bundle small
export const LANGUAGE_MAP: Record<string, { name: string; nativeName: string }> = {
  english: { name: 'English', nativeName: 'English' },
  afrikaans: { name: 'Afrikaans', nativeName: 'Afrikaans' },
  albanian: { name: 'Albanian', nativeName: 'Shqip' },
  amharic: { name: 'Amharic', nativeName: 'አማርኛ' },
  arabic: { name: 'Arabic', nativeName: 'العربية' },
  azerbaijan: { name: 'Azerbaijani', nativeName: 'Azərbaycan' },
  basque: { name: 'Basque', nativeName: 'Euskara' },
  bengali: { name: 'Bengali', nativeName: 'বাংলা' },
  bosnian: { name: 'Bosnian', nativeName: 'Bosanski' },
  catalan: { name: 'Catalan', nativeName: 'Català' },
  cebuano: { name: 'Cebuano', nativeName: 'Cebuano' },
  chinese: { name: 'Chinese', nativeName: '中文' },
  czech: { name: 'Czech', nativeName: 'Čeština' },
  danish: { name: 'Danish', nativeName: 'Dansk' },
  dutch: { name: 'Dutch', nativeName: 'Nederlands' },
  esperanto: { name: 'Esperanto', nativeName: 'Esperanto' },
  estonian: { name: 'Estonian', nativeName: 'Eesti' },
  finnish: { name: 'Finnish', nativeName: 'Suomi' },
  french: { name: 'French', nativeName: 'Français' },
  galician: { name: 'Galician', nativeName: 'Galego' },
  georgian: { name: 'Georgian', nativeName: 'ქართული' },
  german: { name: 'German', nativeName: 'Deutsch' },
  greek: { name: 'Greek', nativeName: 'Ελληνικά' },
  gujarati: { name: 'Gujarati', nativeName: 'ગુજરાતી' },
  haitian: { name: 'Haitian', nativeName: 'Kreyòl Ayisyen' },
  hebrew: { name: 'Hebrew', nativeName: 'עברית' },
  hindi: { name: 'Hindi', nativeName: 'हिन्दी' },
  hungarian: { name: 'Hungarian', nativeName: 'Magyar' },
  icelandic: { name: 'Icelandic', nativeName: 'Íslenska' },
  indonesian: { name: 'Indonesian', nativeName: 'Bahasa Indonesia' },
  irish: { name: 'Irish', nativeName: 'Gaeilge' },
  italian: { name: 'Italian', nativeName: 'Italiano' },
  japanese: { name: 'Japanese', nativeName: '日本語' },
  javanese: { name: 'Javanese', nativeName: 'Basa Jawa' },
  kannada: { name: 'Kannada', nativeName: 'ಕನ್ನಡ' },
  korean: { name: 'Korean', nativeName: '한국어' },
  kurdish: { name: 'Kurdish', nativeName: 'Kurdî' },
  lao: { name: 'Lao', nativeName: 'ລາວ' },
  latin: { name: 'Latin', nativeName: 'Latina' },
  latvian: { name: 'Latvian', nativeName: 'Latviešu' },
  lithuanian: { name: 'Lithuanian', nativeName: 'Lietuvių' },
  macedonian: { name: 'Macedonian', nativeName: 'Македонски' },
  malagasy: { name: 'Malagasy', nativeName: 'Malagasy' },
  malay: { name: 'Malay', nativeName: 'Bahasa Melayu' },
  malayalam: { name: 'Malayalam', nativeName: 'മലയാളം' },
  maltese: { name: 'Maltese', nativeName: 'Malti' },
  maori: { name: 'Maori', nativeName: 'Māori' },
  marathi: { name: 'Marathi', nativeName: 'मराठी' },
  mongolian: { name: 'Mongolian', nativeName: 'Монгол' },
  nepali: { name: 'Nepali', nativeName: 'नेपाली' },
  norwegian: { name: 'Norwegian', nativeName: 'Norsk' },
  persian: { name: 'Persian', nativeName: 'فارسی' },
  polish: { name: 'Polish', nativeName: 'Polski' },
  portuguese: { name: 'Portuguese', nativeName: 'Português' },
  punjabi: { name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ' },
  romanian: { name: 'Romanian', nativeName: 'Română' },
  russian: { name: 'Russian', nativeName: 'Русский' },
  scottish: { name: 'Scottish Gaelic', nativeName: 'Gàidhlig' },
  sinhala: { name: 'Sinhala', nativeName: 'සිංහල' },
  slovakian: { name: 'Slovak', nativeName: 'Slovenčina' },
  slovenian: { name: 'Slovenian', nativeName: 'Slovenščina' },
  spanish: { name: 'Spanish', nativeName: 'Español' },
  sundanese: { name: 'Sundanese', nativeName: 'Basa Sunda' },
  swahili: { name: 'Swahili', nativeName: 'Kiswahili' },
  swedish: { name: 'Swedish', nativeName: 'Svenska' },
  tagalog: { name: 'Tagalog', nativeName: 'Tagalog' },
  tamil: { name: 'Tamil', nativeName: 'தமிழ்' },
  telugu: { name: 'Telugu', nativeName: 'తెలుగు' },
  thai: { name: 'Thai', nativeName: 'ไทย' },
  turkish: { name: 'Turkish', nativeName: 'Türkçe' },
  urdu: { name: 'Urdu', nativeName: 'اردو' },
  uzbek: { name: 'Uzbek', nativeName: "O'zbek" },
  vietnamese: { name: 'Vietnamese', nativeName: 'Tiếng Việt' },
  welsh: { name: 'Welsh', nativeName: 'Cymraeg' },
  xhosa: { name: 'Xhosa', nativeName: 'isiXhosa' },
  yiddish: { name: 'Yiddish', nativeName: 'ייִדיש' },
};

// RTL languages (for layout direction)
export const RTL_LANGUAGES = ['arabic', 'hebrew', 'persian', 'urdu', 'yiddish', 'kurdish'];

/**
 * Dynamically load a language JSON file.
 * Only English is bundled — all others are loaded on demand.
 */
export async function loadLanguage(langCode: string): Promise<void> {
  if (langCode === 'english') return; // Already loaded

  try {
    // Dynamic import — Vite will code-split each locale file
    const module = await import(`./locales/${langCode}.json`);
    i18n.addResourceBundle(langCode, 'translation', module.default, true, true);
    await i18n.changeLanguage(langCode);

    // Set document direction for RTL languages (browser-only)
    const doc = typeof globalThis !== 'undefined' && (globalThis as any).document;
    if (doc) {
      doc.documentElement.dir = RTL_LANGUAGES.includes(langCode) ? 'rtl' : 'ltr';
      doc.documentElement.lang = langCode;
    }
  } catch (err) {
    console.error(`[i18n] Failed to load language: ${langCode}`, err);
  }
}

/**
 * Readable text for a key that has no translation (OEL-181).
 * "present_short" → "Present short". i18next would otherwise render the raw
 * key, which is what reached parents' phones in September 2026. The CI check
 * (scripts/check-i18n-keys.mjs) stops missing keys from merging; this is the
 * safety net for anything it cannot see (dynamic keys, other locales).
 */
export function humaniseKey(key: string): string {
  const words = String(key).replace(/^[a-z0-9_-]+:/i, '').split(/[_\-.\s]+/).filter(Boolean);
  if (words.length === 0) return String(key);
  const text = words.join(' ').toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Get the currently active language code
 */
export function getCurrentLanguage(): string {
  return i18n.language || 'english';
}

/**
 * Get all available languages as an array
 */
export function getAvailableLanguages(): Array<{ code: string; name: string; nativeName: string }> {
  return Object.entries(LANGUAGE_MAP).map(([code, info]) => ({
    code,
    ...info,
  }));
}

// Initialize i18next
i18n
  .use(initReactI18next)
  .init({
    resources: {
      english: { translation: en },
    },
    lng: 'english',              // Default language
    fallbackLng: 'english',      // Fallback if key is missing
    // A key missing from EVERY locale renders as readable text, never as the
    // identifier itself (OEL-181); scripts/check-i18n-keys.mjs stops it merging.
    // i18next also calls this when a defaultValue was used (second argument):
    // an author's explicit default always beats the humanised key.
    parseMissingKeyHandler: (key: string, defaultValue?: string) => {
      if (defaultValue !== undefined && defaultValue !== null && defaultValue !== '') return defaultValue;
      return humaniseKey(key);
    },
    interpolation: {
      escapeValue: false,         // React already escapes
    },
    react: {
      useSuspense: false,         // Don't suspend on lazy loading
    },
  });

export default i18n;
export { i18n };
